import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Users,
  UserRound,
  GitBranch,
  Shield,
  Zap,
  Play,
  CheckCircle2,
  X,
  Save,
} from 'lucide-react';
import {
  designToVerticalFlow,
  flowToDesign,
  applyVerticalLayout,
  kindToNodeType,
  nodeTypeToDefaultKind,
} from './approvalFlowLayout';
import {
  APPROVER_TYPE_OPTIONS,
  MOCK_APPROVER_SUGGESTIONS,
} from './approvalDesignerMockData';

const NODE_ACCENTS = {
  singleApprover: { border: 'border-cyan-400', bg: 'bg-cyan-50', icon: 'text-cyan-600', ring: 'ring-cyan-500' },
  approvalGroup: { border: 'border-violet-400', bg: 'bg-violet-50', icon: 'text-violet-600', ring: 'ring-violet-500' },
  jobLevel: { border: 'border-amber-400', bg: 'bg-amber-50', icon: 'text-amber-600', ring: 'ring-amber-500' },
  aor: { border: 'border-emerald-400', bg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'ring-emerald-500' },
  autoApprove: { border: 'border-emerald-500', bg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'ring-emerald-600' },
  bpmStart: { border: 'border-slate-300', bg: 'bg-white', icon: 'text-slate-500', ring: 'ring-slate-400' },
  bpmEnd: { border: 'border-emerald-400', bg: 'bg-emerald-100/80', icon: 'text-emerald-700', ring: 'ring-emerald-500' },
};

const NODE_ICONS = {
  singleApprover: UserRound,
  approvalGroup: Users,
  jobLevel: GitBranch,
  aor: Shield,
  autoApprove: Zap,
  bpmStart: Play,
  bpmEnd: CheckCircle2,
};

const NODE_TITLES = {
  singleApprover: 'Management Hierarchy',
  approvalGroup: 'Approval Group',
  jobLevel: 'Job Level',
  aor: 'Application Role',
  autoApprove: 'Auto Approve',
  bpmStart: 'Policy Start',
  bpmEnd: 'Complete',
};

function ApprovalStepShell({ type, data, selected, children }) {
  const accent = NODE_ACCENTS[type] || NODE_ACCENTS.singleApprover;
  const Icon = NODE_ICONS[type] || UserRound;
  const isTerminal = type === 'bpmStart' || type === 'bpmEnd';

  return (
    <div
      className={`rounded-xl border-2 px-3 py-2.5 shadow-sm min-w-[180px] max-w-[220px] text-xs transition-shadow ${
        accent.border
      } ${accent.bg} ${selected ? `ring-2 ${accent.ring} ring-offset-1 shadow-md` : ''}`}
    >
      {!isTerminal && (
        <>
          <Handle type="target" position={Position.Top} className="!bg-cyan-600 !w-2.5 !h-2.5 !border-2 !border-white" />
          <Handle type="source" position={Position.Bottom} className="!bg-cyan-600 !w-2.5 !h-2.5 !border-2 !border-white" />
        </>
      )}
      {type === 'bpmStart' && (
        <Handle type="source" position={Position.Bottom} className="!bg-slate-500 !w-2.5 !h-2.5 !border-2 !border-white" />
      )}
      {type === 'bpmEnd' && (
        <Handle type="target" position={Position.Top} className="!bg-emerald-600 !w-2.5 !h-2.5 !border-2 !border-white" />
      )}
      <div className="flex items-start gap-2">
        <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/80 ${accent.icon}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[10px] uppercase tracking-wide text-slate-700">
            {NODE_TITLES[type] || 'Step'}
          </p>
          {children}
        </div>
      </div>
    </div>
  );
}

function makeStepNode(type) {
  return function StepNode({ data, selected }) {
    return (
      <ApprovalStepShell type={type} data={data} selected={selected}>
        <p className="mt-0.5 text-slate-800 truncate font-medium" title={data.label || data.ref}>
          {data.label || data.ref || 'Configure approver'}
        </p>
        {data.routing === 'parallel' && (
          <span className="mt-1 inline-block rounded bg-white/70 px-1.5 py-0.5 text-[9px] font-medium text-violet-700">
            Parallel
          </span>
        )}
      </ApprovalStepShell>
    );
  };
}

const nodeTypes = {
  singleApprover: makeStepNode('singleApprover'),
  approvalGroup: makeStepNode('approvalGroup'),
  jobLevel: makeStepNode('jobLevel'),
  aor: makeStepNode('aor'),
  autoApprove: makeStepNode('autoApprove'),
  bpmStart: makeStepNode('bpmStart'),
  bpmEnd: makeStepNode('bpmEnd'),
};

function MockApproverAutocomplete({ kind, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const wrapRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const suggestions = MOCK_APPROVER_SUGGESTIONS[kind] || MOCK_APPROVER_SUGGESTIONS.lineManager;
  const filtered = suggestions.filter((s) =>
    s.toLowerCase().includes((query || '').toLowerCase())
  );

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search or type approver value…"
        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-60"
        autoComplete="off"
      />
      {open && !disabled && filtered.length > 0 && (
        <ul
          className="absolute z-20 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          role="listbox"
        >
          {filtered.map((item) => (
            <li key={item}>
              <button
                type="button"
                role="option"
                className="w-full px-2.5 py-1.5 text-left text-xs text-slate-800 hover:bg-cyan-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setQuery(item);
                  onChange(item);
                  setOpen(false);
                }}
              >
                {item}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StepEditorDrawer({ node, onClose, onSave }) {
  const [approverType, setApproverType] = useState(node?.data?.kind || 'lineManager');
  const [approverValue, setApproverValue] = useState(node?.data?.ref ?? '');

  useEffect(() => {
    if (!node) return;
    setApproverType(node.data?.kind || nodeTypeToDefaultKind(node.type));
    setApproverValue(node.data?.ref ?? '');
  }, [node]);

  if (!node) return null;

  const isEditable = !['bpmStart', 'bpmEnd'].includes(node.type);

  function handleSave(e) {
    e.preventDefault();
    if (!isEditable) return;
    const kind = approverType;
    const ref = approverValue.trim() || 'n/a';
    const label = `${kind.replace(/([A-Z])/g, ' $1').trim()}${ref && ref !== 'n/a' ? ` · ${ref}` : ''}`;
    onSave(node.id, {
      kind,
      ref,
      label,
      nodeType: kindToNodeType(kind),
    });
    onClose();
  }

  return (
    <>
      <div
        className="absolute inset-0 z-10 bg-slate-900/20 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="absolute right-0 top-0 z-20 flex h-full w-full max-w-sm flex-col border-l border-cyan-200 bg-white shadow-xl transition-transform duration-200 ease-out"
        role="dialog"
        aria-label="Edit approval step"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-cyan-800">Approval step</p>
            <p className="text-sm font-medium text-slate-800 truncate">{node.data?.label || node.id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Close panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isEditable ? (
          <form onSubmit={handleSave} className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              <label className="block text-xs font-medium text-slate-600">
                Approver Type
                <select
                  value={approverType}
                  onChange={(e) => {
                    setApproverType(e.target.value);
                    setApproverValue('');
                  }}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                >
                  {APPROVER_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-xs font-medium text-slate-600">
                Approver Value
                <MockApproverAutocomplete
                  kind={approverType}
                  value={approverValue}
                  onChange={setApproverValue}
                  disabled={approverType === 'autoApprove'}
                />
              </label>

            </div>

            <div className="flex gap-2 border-t border-slate-100 p-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-cyan-700 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-800"
              >
                <Save className="h-4 w-4" />
                Save
              </button>
            </div>
          </form>
        ) : (
          <div className="p-4 text-sm text-slate-600">
            This node is fixed in the workflow ({NODE_TITLES[node.type]}).
          </div>
        )}
      </aside>
    </>
  );
}

function CanvasInner({ design, onDesignChange, readOnly }) {
  const { fitView } = useReactFlow();
  const designKey = useMemo(
    () => JSON.stringify({ stages: design?.stages || [], else: design?.elseAction || null }),
    [design?.stages, design?.elseAction]
  );

  const initial = useMemo(() => designToVerticalFlow(design), [design, designKey]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);
  const [drawerNodeId, setDrawerNodeId] = useState(null);

  useEffect(() => {
    const { nodes: n, edges: e } = designToVerticalFlow(design);
    setNodes(n);
    setEdges(e);
    setDrawerNodeId(null);
    requestAnimationFrame(() => fitView({ padding: 0.25, duration: 200 }));
  }, [design, designKey, setNodes, setEdges, fitView]);

  const drawerNode = nodes.find((n) => n.id === drawerNodeId) || null;

  const pushDesign = useCallback(
    (nextNodes, nextEdges) => {
      if (!onDesignChange || readOnly) return;
      const next = flowToDesign(nextNodes, nextEdges, design);
      onDesignChange(next);
    },
    [design, onDesignChange, readOnly]
  );

  const onNodeClick = useCallback((_event, node) => {
    setDrawerNodeId(node.id);
  }, []);

  const handleSaveStep = useCallback(
    (nodeId, patch) => {
      setNodes((nds) => {
        const nextNodes = nds.map((n) => {
          if (n.id !== nodeId) return n;
          const data = {
            ...n.data,
            kind: patch.kind,
            ref: patch.ref,
            label: patch.label,
          };
          return {
            ...n,
            type: patch.nodeType || n.type,
            data,
          };
        });
        const laidOut = applyVerticalLayout(nextNodes, edges);
        pushDesign(laidOut, edges);
        return laidOut;
      });
    },
    [edges, pushDesign, setNodes]
  );

  return (
    <div className="relative h-full min-h-[420px] w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.25 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={!readOnly}
        proOptions={{ hideAttribution: true }}
        className="bg-slate-50/80"
        defaultEdgeOptions={{ type: 'smoothstep' }}
      >
        <Background color="#cbd5e1" gap={20} />
        <Controls className="!border-cyan-200 !shadow-sm" showInteractive={false} />
        <MiniMap
          className="!border-cyan-200 !bg-white"
          nodeColor={(n) => {
            if (n.type === 'bpmEnd') return '#10b981';
            if (n.type === 'approvalGroup') return '#8b5cf6';
            if (n.type === 'jobLevel') return '#f59e0b';
            if (n.type === 'aor') return '#059669';
            return '#06b6d4';
          }}
        />
      </ReactFlow>

      {drawerNode && (
        <StepEditorDrawer
          node={drawerNode}
          onClose={() => setDrawerNodeId(null)}
          onSave={handleSaveStep}
        />
      )}
    </div>
  );
}

/**
 * Vertical React Flow canvas for Approval Designer — auto layout, Lucide nodes, step editor drawer.
 */
export default function ApprovalDesignerCanvas({ design, onDesignChange, readOnly = false }) {
  if (!design) {
    return (
      <div className="flex h-full min-h-[420px] items-center justify-center rounded-lg border border-dashed border-cyan-200 bg-cyan-50/30 p-8 text-center">
        <p className="text-sm text-slate-500">Select a rule.</p>
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <CanvasInner design={design} onDesignChange={onDesignChange} readOnly={readOnly} />
    </ReactFlowProvider>
  );
}
